import React, { useCallback, useEffect, useState } from "react";
import { fetchAdminApiKeys, postAdminApiKey, revokeAdminApiKey } from "./apiKeysApi";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function statusLabel(status) {
  if (status === "active") return "Active";
  if (status === "expired") return "Expired";
  if (status === "revoked") return "Revoked";
  return status || "—";
}

export default function ApiKeysSettingsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminApiKeys();
      setRows(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not load API keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Name is required.");
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");
    setNewKeyValue("");
    try {
      const result = await postAdminApiKey({
        name: trimmedName,
        expiresAt: expiresAt || null,
      });
      setNewKeyValue(result?.key || "");
      setName("");
      setExpiresAt("");
      setMessage("API key created. Copy it now — it will not be shown again.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not create API key.");
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(row) {
    if (row.status === "revoked") return;
    if (!window.confirm(`Revoke API key "${row.name}"? This cannot be undone.`)) return;

    setBusy(true);
    setError("");
    setMessage("");
    try {
      await revokeAdminApiKey(row.id);
      setMessage(`Revoked API key "${row.name}".`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not revoke API key.");
    } finally {
      setBusy(false);
    }
  }

  async function onCopyNewKey() {
    if (!newKeyValue) return;
    try {
      await navigator.clipboard.writeText(newKeyValue);
      setMessage("API key copied to clipboard.");
    } catch {
      setError("Could not copy to clipboard. Select and copy the key manually.");
    }
  }

  return (
    <section className="plugins-card plugins-card-wide" style={{ padding: "1rem 1.25rem" }}>
      <h3 className="plugins-card-name" style={{ marginTop: 0 }}>
        API keys
      </h3>
      <p className="plugins-card-desc">
        Create API keys for programmatic access. Keys are shown only once at creation. Track who
        created each key and when it expires.
      </p>

      <form onSubmit={onCreate} style={{ marginBottom: "1.25rem" }}>
        <div style={{ display: "grid", gap: "0.75rem", maxWidth: 480 }}>
          <label>
            <span className="email-triggers-muted" style={{ display: "block", marginBottom: 4 }}>
              Name
            </span>
            <input
              type="text"
              className="email-triggers-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CI pipeline, reporting script"
              maxLength={120}
              disabled={busy}
              required
            />
          </label>
          <label>
            <span className="email-triggers-muted" style={{ display: "block", marginBottom: 4 }}>
              Expiration (optional)
            </span>
            <input
              type="datetime-local"
              className="email-triggers-input"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.75rem" }}>
          <button
            type="submit"
            className="email-triggers-button email-triggers-button-primary"
            disabled={busy}
          >
            Create API key
          </button>
          <button
            type="button"
            className="email-triggers-button"
            disabled={busy || loading}
            onClick={load}
          >
            Refresh
          </button>
        </div>
      </form>

      {newKeyValue ? (
        <div
          className="plugins-card"
          style={{
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            background: "var(--pb-surface-elevated, #f8f9fb)",
          }}
        >
          <p className="plugins-card-desc" style={{ marginTop: 0 }}>
            <strong>New API key</strong> — copy and store it securely.
          </p>
          <code
            style={{
              display: "block",
              wordBreak: "break-all",
              padding: "0.5rem",
              marginBottom: "0.5rem",
              fontSize: "0.85rem",
            }}
          >
            {newKeyValue}
          </code>
          <button type="button" className="email-triggers-button" onClick={onCopyNewKey}>
            Copy to clipboard
          </button>
        </div>
      ) : null}

      {loading ? <p className="email-triggers-muted">Loading API keys…</p> : null}
      {message ? (
        <p className="email-triggers-muted" role="status">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="email-triggers-muted" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && rows.length === 0 ? <p className="email-triggers-muted">No API keys yet.</p> : null}

      {!loading && rows.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <table className="email-triggers-table" style={{ width: "100%", minWidth: 760 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Key</th>
                <th>Created by</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.keyHint}</code>
                  </td>
                  <td>{row.createdByEmail || "—"}</td>
                  <td>{formatWhen(row.createdAt)}</td>
                  <td>{row.expiresAt ? formatWhen(row.expiresAt) : "Never"}</td>
                  <td>{formatWhen(row.lastUsedAt)}</td>
                  <td>{statusLabel(row.status)}</td>
                  <td>
                    <button
                      type="button"
                      className="email-triggers-button"
                      disabled={busy || row.status === "revoked"}
                      onClick={() => onRevoke(row)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
